// creates a highlighted section and returns a reference to the surrounding node
import type { QueryClient, QueryKey } from "@tanstack/vue-query";
import { useInfiniteQuery, useMutation } from "@tanstack/vue-query";
import {
  BOOKMARK_POSITION,
  type Collection,
  getRelationship,
  isNovelParagraph,
  NovelBookmark,
  type NovelBookmarkEntity,
} from "~/src/api";
import cloneDeep from "clone-deep";

/**
 * Checks if a given selection contains any highlight elements (`novel-highlight`)
 * within the specified root node. The function traverses the nodes between the
 * start and end of the selection using a `TreeWalker`, collecting the `hid`
 * (highlight ID) of any matching highlight elements encountered.
 *
 * @param selection - The `Selection` object representing the user's selection within the document.
 * @param root - The root `Node` within which to search for highlights. This limits the traversal to ensure
 *               the function doesn't search outside the specified root.
 * @returns A `Set` of unique highlight IDs (`hid`) found within the selection,
 *          or `null` if no highlights are found.
 */
export function containsHighlight(selection: Selection, root: Node) {
  const { start, end } = getStartAndEndFromSelection(selection);
  if (!start || !end) return null;

  // Create a TreeWalker to traverse the nodes between the start and end within the root
  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT + NodeFilter.SHOW_ELEMENT,
  );
  walker.currentNode = start;

  const matchingHighlightIds = new Set<string>();

  // check if start is in a highlight
  if (start instanceof Text) {
    const startParent = start.parentNode;
    if (
      startParent instanceof Element &&
      startParent.classList.contains("novel-highlight")
    ) {
      const hid = startParent.getAttribute("hid");
      if (hid) matchingHighlightIds.add(hid);
    }
  }

  // check if end is in a highlight
  if (end instanceof Text) {
    const endParent = end.parentNode;
    if (
      endParent instanceof Element &&
      endParent.classList.contains("novel-highlight")
    ) {
      const hid = endParent.getAttribute("hid");
      if (hid) matchingHighlightIds.add(hid);
    }
  }

  // Traverse from the start node to the end node
  let node: Node | null = walker.currentNode;
  while (node && node !== end) {
    if (node instanceof Element && node.classList.contains("novel-highlight")) {
      const hid = node.getAttribute("hid");
      if (hid) matchingHighlightIds.add(hid);
    }
    node = walker.nextNode();
  }

  return matchingHighlightIds;
}

/**
 * Finds the first highlight element (`novel-highlight`) between a given child node and a parent node.
 *
 * This function walks up the DOM tree from the `child` node toward the `parent`, checking each
 * ancestor node to see if it is a highlight element (i.e. it has the class `novel-highlight`).
 * If a highlight element is found, its `hid` attribute (highlight ID) is returned.
 *
 * @param child - The starting `Node` to search upward from (typically a leaf or text node).
 * @param parent - The ancestor `Node` at which to stop searching. This node is not included in the check.
 * @returns The `hid` attribute of the first `novel-highlight` element found between `child` and `parent`,
 *          or `null` if no such element is found or either argument is `null`.
 */
export function highlightBetween(child: Node | null, parent: Node | null) {
  if (!child || !parent) return null;

  let node: null | Node = child;
  while (node && node !== parent) {
    if (node instanceof Element && node.classList.contains("novel-highlight")) {
      return node.getAttribute("hid");
    }
    node = node.parentNode;
  }

  return null;
}

/**
 * Removes all highlight elements (with class `novel-highlight`) that match a specific `highlightId`,
 * by unwrapping them and reinserting their child nodes directly into the parent element.
 *
 * This is a naive unwrapping method that assumes the highlight elements are non-nested and
 * replaces the parent’s children with a flattened structure where the highlight elements are removed.
 *
 * @param highlightId - The unique highlight ID (`hid` attribute) used to identify which highlights to remove.
 * @param searchRoot - The root `Element` within which to search for matching highlight elements.
 */
export function removeHighlight(highlightId: string, searchRoot: Element) {
  searchRoot
    .querySelectorAll(`.novel-highlight[hid="${highlightId}"]`)
    .forEach((highlight) => {
      const parent = highlight.parentElement;
      if (!parent) return;

      const newChildren = Array.from(parent.childNodes).flatMap((node) => {
        if (node === highlight) return Array.from(highlight.childNodes);
        return node;
      });

      parent.replaceChildren(...newChildren);
      parent.normalize();
    });
}

/**
 * Performs the same task as `removeHighlight` without specifying an ID.
 * @param searchRoot - The root `Element` within which to search for matching highlight elements.
 */
export function removeAllHighlights(searchRoot: Element) {
  searchRoot.querySelectorAll(`.novel-highlight`).forEach((highlight) => {
    const parent = highlight.parentElement;
    if (!parent) return;

    const newChildren = Array.from(parent.childNodes).flatMap((node) => {
      if (node === highlight) return Array.from(highlight.childNodes);
      return node;
    });

    parent.replaceChildren(...newChildren);
    parent.normalize();
  });
}

/**
 * Applies a highlight to the current text selection by wrapping the selected content with custom elements.
 *
 * > Note: This function currently does not support selections that cross paragraph boundaries.
 * If such a case is detected, it throws an error.
 *
 * @param rootNode - An optional root node (e.g., container for all the paragraphs) used to validate whether
 *                   the selection is within a single paragraph. If omitted, the selection is assumed invalid.
 * @param selection - An optional `Selection` object (e.g., from `window.getSelection()`).
 *                    If not provided, no action is taken.
 *
 * @returns An array of created highlight elements that were applied to the selection.
 *          Returns `undefined` if no valid selection or nodes are found.
 *
 * @throws Throws an error if the selection crosses child boundaries within the `rootNode`.
 */
export function highlightSelection(
  rootNode?: Node,
  selection?: Selection | null,
) {
  if (!selection) return;

  const { start, startOffset, endOffset, end } =
    getStartAndEndFromSelection(selection);

  if (!start || !end) return;

  const range = new Range();
  range.setStart(start, startOffset);
  range.setEnd(end, endOffset);
  const commonParent = getParentElement(range.commonAncestorContainer);

  // We don't support cross paragraph highlights, these have yet to show functional benefit
  if (commonParent === rootNode || !rootNode) {
    throw new Error("Selection crosses paragraph boundaries.");
  }

  return surroundRange(range);
}

export function highlightFromEntity(
  highlight: NovelBookmarkEntity,
  root: Element,
) {
  if (highlight.attributes.position === BOOKMARK_POSITION)
    throw new Error("Entity is not a highlight");
  const associatedParagraph = getRelationship(
    highlight,
    "novel_chapter_paragraph",
  );
  const paragraph = root.querySelector(`[id="${associatedParagraph.id}"]`);
  if (!paragraph) throw new Error("Paragraph specified by highlight not found");

  // figure out the selected range in dom
  const [offset, length] = highlight.attributes.position
    .split(";")
    .map((s) => parseInt(s));

  const start = getChildAndOffsetFromParentOffset(paragraph, offset);
  const end = getChildAndOffsetFromParentOffset(paragraph, offset + length);

  const range = new Range();
  range.setStart(start.node, start.offsetInNode);
  range.setEnd(end.node, end.offsetInNode);

  // highlight the elements and tag them with the highlight id
  const elems = surroundRange(range);
  elems.forEach((elem) => elem.setAttribute("hid", highlight.id));
  return elems;
}

/**
 * Surrounds the content of a `Range` with a custom element (e.g., for highlighting),
 * handling edge cases like partial selections that split text nodes or intersect existing highlights.
 *
 * This function ensures that the range is fully surrounded even when the start or end
 * of the selection is inside a text node or partially inside an existing highlight.
 * It can split text nodes and create additional `Range` objects as needed.
 *
 * It also removes nested highlight elements to prevent redundant markup.
 *
 * @param range - The DOM `Range` object representing the selection to wrap.
 * @param createElement - Optional factory function to create the wrapper element. Defaults to `createHighlightElement()`.
 * @returns An array of wrapper elements (e.g., `<mark>`) that were created around the range.
 *
 * @throws errors if DOM operations like `surroundContents()` fail (e.g., due to invalid hierarchy).
 */
function surroundRange(
  range: Range,
  createElement: () => Element = createHighlightElement,
) {
  const {
    startContainer: start,
    startOffset,
    endContainer: end,
    endOffset,
    commonAncestorContainer: commonParent,
  } = range;
  const surroundRanges = [range];

  // handle partial highlights
  if (isCaretPartial(start, startOffset, commonParent)) {
    const parentElem = getParentElement(start);
    // expand selection if it crosses highlight boundaries?
    if (parentElem.classList.contains("novel-highlight")) {
      range.setStartBefore(parentElem);
    } else {
      // handle splitting the partial node here
      // if text, split the text nodes and make that a separate range
      // make the start of the current range after the parent elem
      if (start instanceof Text) {
        const splitNode = start.splitText(startOffset);

        range.setStartAfter(parentElem);

        const splitRange = new Range();
        splitRange.setStartBefore(splitNode);
        splitRange.setEndAfter(splitNode);
        surroundRanges.push(splitRange);
      }
    }
  } else if (startOffset === 0 && start.parentNode && start !== end) {
    // handles edge case:
    // if you select from the 0th char in a text node whose parent is not the same as the parent of the end node,
    // it would fail to parse and throw a range error
    range.setStartBefore(start.parentNode);
  }
  if (isCaretPartial(end, endOffset, commonParent)) {
    const parentElem = getParentElement(end);
    // expand selection if it crosses highlight boundaries?
    if (parentElem.classList.contains("novel-highlight")) {
      range.setEndAfter(parentElem);
    } else {
      // handle splitting the partial node here
      // if text, split the text nodes and make that a separate range
      // make the end of the current range before the parent elem
      if (end instanceof Text) {
        const splitNode = end.splitText(endOffset).previousSibling!;

        range.setEndBefore(parentElem);

        const splitRange = new Range();
        splitRange.setStartBefore(splitNode);
        splitRange.setEndAfter(splitNode);
        surroundRanges.push(splitRange);
      }
    }
  }

  const marks = [];

  try {
    for (const surroundRange of surroundRanges) {
      // create the mark tag that we will use
      const mark = createElement();
      marks.push(mark);

      surroundRange.surroundContents(mark);

      // remove nested highlighting
      const newChildren = Array.from(mark.childNodes).flatMap((el) => {
        if (el instanceof Element && el.classList.contains("novel-highlight"))
          return Array.from(el.childNodes);
        return el;
      });

      mark.replaceChildren(...newChildren);
    }
  } catch (e) {
    console.log(
      isCaretPartial(start, startOffset, commonParent),
      isCaretPartial(end, endOffset, commonParent),
    );
    console.error(e);
  }

  return marks;
}

const createHighlightElement = () => {
  const mark = document.createElement("span");
  mark.classList.add("novel-highlight");
  return mark;
};

// dom tree helpers
/**
 * Traverses up the DOM tree to find the closest parent Element of a given Node.
 * @param node - The starting Node from which to begin the search.
 * @param includeRoot - If `true` and the starting node is an Element, it will be returned immediately. Defaults to `true`.
 * @param maxDepth - The maximum number of parent levels to traverse. Defaults to 32.
 * @returns The closest parent Element node.
 * @throws If no parent Element is found within `maxDepth` levels or if a parent cannot be located.
 */
function getParentElement(
  node: Node,
  includeRoot = true,
  maxDepth = 32,
): Element {
  if (includeRoot && node instanceof Element) return node;
  let parent = node;
  let iter = 0;
  do {
    if (iter > maxDepth)
      throw new Error("Max depth to search for parent elem reached");
    if (!node.parentNode) throw new Error("Unable to locate parent elem");

    parent = node.parentNode;
    iter++;
  } while (!(parent instanceof Element));

  return parent;
}

/**
 * Determines whether a caret (text cursor) position is "partial" relative to a common ancestor node.
 * A caret is considered partial if:
 * - The current node is not the common ancestor itself,
 * - The node's parent is not the common ancestor, and
 * - The caret is not at the beginning of the node (offset !== 0).
 * @param node - The node where the caret is positioned.
 * @param offset - The character offset within the node.
 * @param commonAncestor - The reference ancestor node to compare against.
 * @returns `true` if the caret is considered partial, `false` otherwise.
 */
function isCaretPartial(
  node: Node,
  offset: number,
  commonAncestor: Node,
): boolean {
  if (node === commonAncestor) return false;
  return getParentElement(node) !== commonAncestor && offset !== 0;
}

/**
 * Converts a character offset relative to a child element into an offset relative to a parent element.
 * @param parent The ancestor HTMLElement.
 * @param child The descendant HTMLElement.
 * @param offsetInChild The character offset inside the child.
 * @returns The character offset relative to the parent's text content.
 */
export function getOffsetRelativeToParent(
  parent: Node,
  child: Node,
  offsetInChild: number,
): number {
  if (!parent.contains(child)) return 0;

  let offset = 0;
  const walker = document.createTreeWalker(parent, NodeFilter.SHOW_TEXT, null);
  let found = false;

  while (walker.nextNode()) {
    const node = walker.currentNode as Text;

    if (child.contains(node)) {
      offset += offsetInChild;
      found = true;
      break;
    } else {
      offset += node.textContent?.length ?? 0;
    }
  }
  if (!found) return 0;
  return offset;
}

/**
 * Given a parent element and a character offset into its text content,
 * returns the deepest child node (typically a Text node) and the offset inside it.
 *
 * @param parent The root HTMLElement.
 * @param offset The character offset relative to the parent's text content.
 * @returns An object containing the target node and the offset inside it.
 */
function getChildAndOffsetFromParentOffset(
  parent: Node,
  offset: number,
): { node: Node; offsetInNode: number } {
  const walker = document.createTreeWalker(parent, NodeFilter.SHOW_TEXT, null);
  let currentOffset = 0;

  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    const length = node.textContent?.length ?? 0;

    if (
      offset <= currentOffset + length &&
      node instanceof Text &&
      length > 0
    ) {
      return {
        node,
        offsetInNode: offset - currentOffset,
      };
    }

    currentOffset += length;
  }

  throw new Error("Offset exceeds the parent's total text content length.");
}

/**
 * Returns the start and end nodes and offsets from a `Selection` object,
 * taking the selection direction into account.
 * @param selection - The Selection object from which to extract the start and end points.
 * @returns An object containing:
 *  - `start`: The starting Node of the selection.
 *  - `startOffset`: The character offset within the start node.
 *  - `end`: The ending Node of the selection.
 *  - `endOffset`: The character offset within the end node.
 */
export function getStartAndEndFromSelection(selection: Selection) {
  const {
    direction: selDir,
    anchorNode,
    focusNode,
    anchorOffset,
    focusOffset,
  } = selection;

  let direction = selDir;
  if (focusNode && !direction) {
    const position = anchorNode?.compareDocumentPosition(focusNode);
    // position == 0 if nodes are the same
    if (
      (!position && anchorOffset > focusOffset) ||
      position === Node.DOCUMENT_POSITION_PRECEDING
    )
      direction = "backward";
    else direction = "forward";
  }

  const start = direction === "forward" ? anchorNode : focusNode;
  const startOffset = direction === "forward" ? anchorOffset : focusOffset;
  const end = direction === "forward" ? focusNode : anchorNode;
  const endOffset = direction === "forward" ? focusOffset : anchorOffset;

  return {
    start,
    startOffset,
    end,
    endOffset,
  };
}

// highlight manager stuff
type InfQueryDataType = {
  pages: Collection<NovelBookmarkEntity>[];
  pageParams: number[];
};

type HighlightCreateParams = {
  chapterId: string;
  paragraphId: string;
  position: string;
  comment?: string;
  noRefresh?: boolean;
};

export const useNovelHighlightManager = (
  queryClient: QueryClient,
  titleId: MaybeRefOrGetter<string>,
  chapterId: MaybeRefOrGetter<string>,
) => {
  const queryKey = [
    "novel_reader_highlights",
    titleId,
    chapterId,
  ] satisfies QueryKey;

  const highlightQuery = useInfiniteQuery(
    {
      queryKey: queryKey,
      queryFn: async ({ pageParam, queryKey }) => {
        const [_, titleId, chapterId] = extractQueryKey(queryKey);
        if (!titleId)
          throw new Error("Title id required for highlight manager");
        if (!chapterId)
          throw new Error("Chapter id required for highlight manager");

        const resp = await NovelBookmark.find(
          {
            titleId: titleId,
            novelChapterId: chapterId,
            offset: pageParam,
            order: {
              createdAt: "desc",
            },
            includes: ["novel_chapter", "novel_chapter_paragraph"],
            showSelections: true,
          },
          await getTokenOrThrow(),
        );

        return resp;
      },
      initialPageParam: 0,
      getNextPageParam: (prev, pages, pageParam) => {
        if (prev.meta.total > prev.meta.offset + prev.meta.limit)
          return prev.meta.offset + prev.meta.limit;
        return null;
      },
      select: (data) => ({
        type: "collection",
        data: data.pages
          .flatMap((page) => page.data)
          .filter((bm) => bm.attributes.position !== BOOKMARK_POSITION),
        meta: data.pages[0].meta,
      }),
      staleTime: 1000 * 60 * 5,
    },
    queryClient,
  );

  const createMutation = useMutation({
    mutationFn: async (params: HighlightCreateParams) =>
      await NovelBookmark.create(
        {
          position: params.position,
          comment: params.comment,
          novelChapterId: params.chapterId,
          novelChapterParagraphId: params.paragraphId,
        },
        await getTokenOrThrow(),
      ),
    onMutate: async (params: HighlightCreateParams) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (oldValue?: InfQueryDataType) => {
        const base: InfQueryDataType = oldValue
          ? cloneDeep(oldValue)
          : {
              pages: [
                {
                  type: "collection",
                  data: [],
                  meta: {
                    total: 0,
                    offset: 0,
                    limit: 50,
                  },
                },
              ],
              pageParams: [0],
            };

        base.pages[0].data.push({
          id: `placeholder_${params.paragraphId}`,
          type: "novel_chapter_bookmark",
          attributes: {
            position: params.position,
            comment: params.comment || null,
            createdAt: new Date().toISOString(),
          },
          relationships: [
            {
              type: "novel_chapter_paragraph",
              id: params.paragraphId,
            },
            {
              type: "novel_chapter",
              id: params.chapterId,
            },
          ],
        });
        base.pages.forEach((page) => (page.meta.total += 1));
        return base;
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: (data, err, variables) => {
      // if (!variables.noRefresh)
      queryClient.invalidateQueries({ queryKey: queryKey });
    },
  });
  const deleteMutation = useMutation({
    mutationFn: async (params: { highlightId: string; noRefresh?: boolean }) =>
      NovelBookmark.delete(params.highlightId, 0, await getTokenOrThrow()),
    onMutate: async (params: { highlightId: string; noRefresh?: boolean }) => {
      await queryClient.cancelQueries({ queryKey: queryKey });
      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (oldValue?: InfQueryDataType) => {
        if (!oldValue) return undefined;
        const base = cloneDeep(oldValue);
        base.pages.forEach((page) => {
          const index = page.data.findIndex(
            (bm) => bm.id === params.highlightId,
          );
          if (index >= 0) {
            page.data.splice(index, 1);
            page.meta.total -= 1;
          }
        });
        return base;
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: (data, err, variables) => {
      if (!variables.noRefresh)
        queryClient.invalidateQueries({ queryKey: queryKey });
    },
  });
  const editMutation = useMutation({
    mutationFn: async (params: { highlightId: string; comment?: string }) => {
      return await NovelBookmark.update(
        params.highlightId,
        { comment: params.comment },
        await getTokenOrThrow(),
      );
    },
    onMutate: async ({ highlightId, comment }) => {
      await queryClient.cancelQueries({ queryKey });
      const highlight = getHighlightFromQuery(
        queryClient,
        queryKey,
        highlightId,
      );
      updateHighlightOnQuery(queryClient, queryKey, highlightId, comment);
      return highlight;
    },
    onError: (_error, _params, oldHighlight) => {
      if (!oldHighlight) return;
      replaceHighlightOnQuery(queryClient, queryKey, oldHighlight);
    },
    onSuccess: (highlight) => {
      const oldHighlight = getHighlightFromQuery(
        queryClient,
        queryKey,
        highlight.id,
      );
      if (!oldHighlight) return;

      highlight.relationships = oldHighlight.relationships;
      replaceHighlightOnQuery(queryClient, queryKey, highlight);
    },
  });

  return {
    highlightQuery,
    createMutation,
    deleteMutation,
    editMutation,
    invalidate: () => queryClient.invalidateQueries({ queryKey: queryKey }),
  };
};

function getHighlightFromQuery(
  queryClient: QueryClient,
  queryKey: QueryKey,
  highlightId: string,
) {
  const [pageIndex, highlightIndex] = getIndicesForHighlight(
    queryClient,
    queryKey,
    highlightId,
  );
  if (pageIndex === -1 || highlightIndex === -1) return;

  const data = queryClient.getQueryData(queryKey) as
    | InfQueryDataType
    | undefined;
  if (!data) return;

  return data.pages[pageIndex].data[highlightIndex];
}

function updateHighlightOnQuery(
  queryClient: QueryClient,
  queryKey: QueryKey,
  highlightId: string,
  comment: string | undefined,
) {
  const [pageIndex, highlightIndex] = getIndicesForHighlight(
    queryClient,
    queryKey,
    highlightId,
  );
  if (pageIndex === -1 || highlightIndex === -1) return;

  queryClient.setQueryData(queryKey, (oldData: InfQueryDataType) => {
    const clone = cloneDeep(oldData);
    clone.pages[pageIndex].data[highlightIndex].attributes.comment =
      comment ?? null;
    return clone;
  });
}

function replaceHighlightOnQuery(
  queryClient: QueryClient,
  queryKey: QueryKey,
  highlight: NovelBookmarkEntity,
) {
  const [pageIndex, highlightIndex] = getIndicesForHighlight(
    queryClient,
    queryKey,
    highlight.id,
  );
  if (pageIndex === -1 || highlightIndex === -1) return;

  queryClient.setQueryData(queryKey, (oldData: InfQueryDataType) => {
    const clone = cloneDeep(oldData);
    clone.pages[pageIndex].data[highlightIndex] = highlight;
    return clone;
  });
}

function getIndicesForHighlight(
  queryClient: QueryClient,
  queryKey: QueryKey,
  highlightId: string,
): [number, number] {
  const data = queryClient.getQueryData(queryKey) as
    | InfQueryDataType
    | undefined;
  if (!data) return [-1, -1];

  const pageIndex = data.pages.findIndex((page) =>
    page.data.find((b) => b.id === highlightId),
  );
  if (pageIndex === -1) return [-1, -1];
  const highlightIndex = data.pages[pageIndex].data.findIndex(
    (b) => b.id === highlightId,
  );
  if (highlightIndex === -1) return [-1, -1];

  return [pageIndex, highlightIndex];
}
